import type { InventoryVehicle } from '@carvana/merch-contracts';
import { useContext, useMemo } from 'react';
import ConditionalTile from '~/constants/srp/ConditionalTile';
import { useFavoritesIds } from '~/context/common/UserContext';
import InventoryContext from '~/context/srp/InventoryContext';
import type { InventoryConditionalTile } from '~/contracts/srp';
import useIsOpenSearch from '~/hooks/srp/selectors/useIsOpenSearch';
import { injectConditionalTiles } from '~/hooks/srp/useConditionalTiles';

export type Tile = (InventoryVehicle | InventoryConditionalTile) & {
  pageIndex: number;
};

export default function useTiles(): Tile[] {
  const { inventory, loadMoreResultsTileInfo } = useContext(InventoryContext);
  const favoritesIds = useFavoritesIds();
  const isOpenSearch = useIsOpenSearch();

  return useMemo<Tile[]>(() => {
    if (!inventory.isLoaded || !inventory.data.inventory.vehicles.length) {
      return [];
    }

    const allVehicles = inventory.data.inventory.vehicles.map<InventoryVehicle>(
      vehicle => ({
        ...vehicle,
        isFavorite: favoritesIds.some(v => v === vehicle.vehicleId)
      })
    );

    const excludeNewCarConfiguratorInOpenSearch = (
      tile: InventoryConditionalTile
    ) => !(isOpenSearch && tile.key === ConditionalTile.NewCarConfigurator);

    // Conditional tiles are positioned relative to their own page's results,
    // not the accumulated total, so each page's vehicles + conditional tiles
    // are injected as their own chunk and concatenated - this keeps earlier
    // pages' tiles at the positions they already rendered at.
    //
    // loadMoreResultsTileInfo.data already includes the landing page (seeded
    // in InventoryContext / useGetInventory), in load order - unshifted for
    // Load Previous, pushed for Load More - so it can be read directly instead
    // of inferring any page's size.
    const pageChunks = loadMoreResultsTileInfo.data.map(page => ({
      conditionalTiles: page.conditionalTiles.filter(
        excludeNewCarConfiguratorInOpenSearch
      ),
      vehicleCount: page.vehicleCount
    }));

    let cursor = 0;

    return pageChunks.flatMap(
      ({ conditionalTiles, vehicleCount }, pageIndex) => {
        const vehiclesInChunk = allVehicles.slice(
          cursor,
          cursor + vehicleCount
        );

        cursor += vehicleCount;

        return injectConditionalTiles(vehiclesInChunk, conditionalTiles).map(
          tile => ({ ...tile, pageIndex })
        );
      }
    );
  }, [
    favoritesIds,
    inventory.data.inventory.vehicles,
    inventory.isLoaded,
    isOpenSearch,
    loadMoreResultsTileInfo.data
  ]);
}
